import { FC, ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';

/**
 * Feld-Label UEBER dem Feld — der Dialog-Standard (DESIGN_SYSTEM.md
 * „Dialoge (Runde 3b)", Referenz `AddPoolMemberDialog`): 13px,
 * `text.secondary`, 4px Abstand zum Feld. Keine MUI-Floating-Labels in
 * Dialogen.
 *
 * Design-Paket 3 (11.09.2026): das Muster stand in jedem Dialog erneut
 * ausgeschrieben (`AddPoolMemberDialog`, `BookMeetingDialog` als lokaler
 * `Feldblock`, `DealLostDialog` …). Jetzt an EINER Stelle, damit die
 * Dialogfelder nicht wieder auseinanderlaufen. Das Feld darunter bleibt die
 * Hauskomponente (`FilterSelect`, `TextField sx={dsTextFieldSx}`).
 *
 * Barrierefreiheit: das Label ist eine Typography, kein `<label>` — das Feld
 * traegt denselben Text als `aria-label` (`slotProps.htmlInput` am
 * TextField, `aria-label` am FilterSelect). Genau das findet
 * `getByLabelText` in den Tests.
 *
 * Gegenstueck im Wizard ist `WizardFeld` (SectionLabel, uppercase 11px) —
 * dort gilt das Formular-Muster der Steps, hier das der Dialoge.
 *
 * ADDITIV seit 19.10.2026 (Design-Fix Vertriebsservice-Portal): `optional`
 * haengt „· optional" an das Label, `hinweis` setzt eine Erlaeuterung unter
 * das Feld. Damit loest Feldblock das lokale `PflegeFeld` der Portal-Dialoge
 * ab — kein drittes Label-Muster (design-system.md §2 „Dialog-Felder").
 * Ohne die beiden Props ist die Optik unveraendert.
 */
interface FeldblockProps {
  label: string;
  /** Zeigt „· optional" hinter dem Label — statt es in den Text zu schreiben. */
  optional?: boolean;
  /** Erlaeuterung unter dem Feld (11px, wie der Hilfetext von `dsTextFieldSx`). */
  hinweis?: string;
  children: ReactNode;
}

const Feldblock: FC<FeldblockProps> = ({ label, optional = false, hinweis, children }) => (
  <Stack gap={1} sx={{ minWidth: 0 }}>
    <Typography variant="label">
      {label}
      {optional && (
        <Box component="span" sx={{ color: 'text.muted', fontWeight: 400 }}> · optional</Box>
      )}
    </Typography>
    {children}
    {hinweis && (
      <Typography variant="klein">{hinweis}</Typography>
    )}
  </Stack>
);

export default Feldblock;
